import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'

import { DealerPerformanceTable, DealerPerf } from './DealerPerformanceTable'

// One node of a drill hierarchy. Group nodes (country/state/dealer) carry
// children; leaf nodes (city/boat) don't. Boat leaves additionally carry the
// per-boat fields the "boats" row style renders (status, location, model).
export interface DrillNode {
    label: string
    kind?: string
    secondary?: string // boat model (boats-mode secondary line)
    id?: string // device serial (boat leaves — map focus)
    status?: string
    city?: string
    state?: string
    country?: string
    activeBoats: number
    totalHours: number
    avgHours: number
    totalHoursDeltaPct?: number | null
    avgHoursDeltaPct?: number | null
    drillable?: boolean
    children?: DrillNode[]
}

interface Props {
    data: DrillNode[]
    /** kind -> column header / empty-state noun, e.g. {country:'Country'}. */
    kindLabels: Record<string, string>
    /** Breadcrumb root label (e.g. "World", "All Dealers"). */
    rootLabel: string
    rootIcon?: React.ReactNode
    rangeLabel?: string
    /** Rows to show per level; 'all' shows everything the backend sent. */
    count?: number | 'all'
    /** Client-side text filter applied to the current level. */
    search?: string
    /** Rendered without its own card chrome (shell owns title/tabs/controls). */
    embedded?: boolean
    title?: string
    tabs?: React.ReactNode
    /** Boat leaf clicked (focus it on the fleet map). */
    onBoatClick?: (row: DealerPerf) => void
}

/**
 * Generic breadcrumb drill-down over a nested hierarchy, reusing
 * DealerPerformanceTable for the actual rows. Group levels render in 'group'
 * mode (Active / Total Hrs / Avg) with drill chevrons; 'boat' leaf levels
 * render in 'boats' mode (status + location, no sparkline) and clicking focuses
 * the map. Used for both the Location (country▸state▸city) and Dealer
 * (dealer▸boats) tabs. Owns drill path as labels so it survives data refreshes.
 */
export const DrillTable: React.FC<Props> = ({
    data,
    kindLabels,
    rootLabel,
    rootIcon,
    rangeLabel,
    count = 10,
    search = '',
    embedded,
    title,
    tabs,
    onBoatClick,
}) => {
    const [path, setPath] = useState<string[]>([])

    // Walk the path against current data (stale labels stop the walk).
    const crumbs: DrillNode[] = []
    let level: DrillNode[] = data
    for (const lbl of path) {
        const node = level.find((n) => n.label === lbl)
        if (!node || !node.children?.length) break
        crumbs.push(node)
        level = node.children
    }
    const depth = crumbs.length

    // Filter (text) then limit (count). Data arrives pre-sorted by hours.
    const q = search.trim().toLowerCase()
    let rowsNodes = q
        ? level.filter(
              (n) =>
                  n.label.toLowerCase().includes(q) ||
                  (n.secondary || '').toLowerCase().includes(q) ||
                  (n.city || '').toLowerCase().includes(q) ||
                  (n.state || '').toLowerCase().includes(q) ||
                  (n.country || '').toLowerCase().includes(q)
          )
        : level
    const totalAtLevel = rowsNodes.length
    if (count !== 'all') rowsNodes = rowsNodes.slice(0, count)

    const currentKind = level[0]?.kind || ''
    const isBoatLevel = currentKind === 'boat'
    const entityLabel = kindLabels[currentKind] || 'Name'

    const rows: DealerPerf[] = rowsNodes.map((n) => ({
        dealer: n.label,
        model: n.secondary,
        id: n.id,
        status: n.status,
        city: n.city,
        state: n.state,
        country: n.country,
        activeBoats: n.activeBoats,
        totalHours: n.totalHours,
        avgHours: n.avgHours,
        totalHoursDeltaPct: n.totalHoursDeltaPct,
        avgHoursDeltaPct: n.avgHoursDeltaPct,
        drillable: !!n.drillable && !!n.children?.length,
    }))

    const handleRowClick = (row: DealerPerf) => {
        const node = level.find((n) => n.label === row.dealer)
        if (node?.drillable && node.children?.length) {
            setPath([...crumbs.map((c) => c.label), node.label])
        } else if (node?.kind === 'boat' && onBoatClick) {
            onBoatClick(row)
        }
    }

    const breadcrumb = (
        <Box
            sx={{
                px: 2.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 0.25,
                fontSize: 12,
                minHeight: 30,
            }}
        >
            <Crumb
                onClick={depth > 0 ? () => setPath([]) : undefined}
                current={depth === 0}
                icon={rootIcon}
                label={rootLabel}
            />
            {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1
                return (
                    <React.Fragment key={c.label}>
                        <ChevronRight
                            sx={{
                                fontSize: 14,
                                color: 'text.disabled',
                                mx: 0.25,
                            }}
                        />
                        <Crumb
                            label={c.label}
                            current={isLast}
                            onClick={
                                isLast
                                    ? undefined
                                    : () =>
                                          setPath(
                                              crumbs
                                                  .slice(0, i + 1)
                                                  .map((x) => x.label)
                                          )
                            }
                        />
                    </React.Fragment>
                )
            })}
            {count !== 'all' && totalAtLevel > count && (
                <Typography
                    component="span"
                    sx={{ ml: 1, fontSize: 11, color: 'text.disabled' }}
                >
                    · top {count} of {totalAtLevel}
                </Typography>
            )}
        </Box>
    )

    return (
        <DealerPerformanceTable
            data={rows}
            rangeLabel={rangeLabel}
            title={title}
            entityLabel={entityLabel}
            tabs={tabs}
            breadcrumb={breadcrumb}
            embedded={embedded}
            mode={isBoatLevel ? 'boats' : 'group'}
            hideSparkline={isBoatLevel}
            onRowClick={handleRowClick}
        />
    )
}

// A breadcrumb segment. Clickable ancestors are accent-colored; the current
// (last) segment is plain and inert.
const Crumb: React.FC<{
    label: string
    onClick?: () => void
    current?: boolean
    icon?: React.ReactNode
}> = ({ label, onClick, current, icon }) => (
    <Typography
        component="span"
        onClick={onClick}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 12,
            fontWeight: current ? 700 : 600,
            color: current ? 'text.primary' : 'primary.main',
            cursor: onClick ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            '&:hover': onClick ? { textDecoration: 'underline' } : undefined,
        }}
    >
        {icon}
        {label}
    </Typography>
)

export default DrillTable
